/**
 * SubscriptionSettings — subscription status card for `/settings/subscription`.
 *
 * Fetches GET /api/users/subscription-settings via `useFetch<T>()` and
 * renders differentiated states: Free, active user-paid Plus (billing
 * metadata + cancel affordance), deferred-cancellation (exact copy + end
 * date + `Reactivar Plus` upsell), and corporate Plus (partner benefit,
 * optional discounted amount). For cancellable paid Plus it shows an
 * `AlertDialog` confirmation that fires the `cancel-subscription` PostHog
 * event on explicit confirm, calls `POST /api/subscription/cancel` through
 * the central API layer, and shows a success/error toast. Auto-activated
 * corporate Plus remains non-cancellable.
 */
import { useState } from "react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useFetch } from "modules/main/hooks/useFetch";
import { apiRequest } from "modules/main/services/apiRequest";
import {
  endPointSubscriptionCancel,
  endPointSubscriptionSettings,
} from "modules/main/services/endpoints";
import { AcquirePlusDialog } from "modules/subscription/components/AcquirePlusDialog";
import { CancelResult, SubscriptionSettingsResponse } from "../types/subscription-settings";

/**
 * Pending cancellation state after a successful deferred cancellation.
 * Rendered in place of the cancel button so the user sees when access ends.
 */
interface PendingCancellation {
  endsAt: string;
}

/** Exact copy for a deferred (cancellation_pending) subscription. */
const CANCELLED_COPY =
  "Cancelaste tu suscripción a Plus. Mantienes acceso completo hasta el término de tu período actual, y después tu cuenta pasa automáticamente al plan gratuito.";

/** Returns only the date part from Flow/ISO date-time strings. */
const formatSubscriptionEndDate = (
  value: string | null | undefined
): string => {
  if (!value) return "";
  return value.split(/[ T]/)[0];
};

/**
 * Formats a calendar date into a Spanish long date (e.g. "28 de agosto de 2026").
 * Handles both `YYYY-MM-DD HH:mm:ss` and ISO `YYYY-MM-DDTHH:mm:ss+00:00`
 * values by using only the date part — billing dates are calendar dates, not
 * moments, so timezone shifts must not change the rendered day.
 *
 * @param value Date-time string, or null/undefined.
 * @returns Formatted Spanish date, or "" when the value is unusable.
 */
const formatLongDate = (value: string | null | undefined): string => {
  if (!value) return "";
  const datePart = value.split(/[ T]/)[0];
  if (!datePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return datePart;
  return new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

/**
 * Formats an integer CLP amount with currency and billing interval,
 * e.g. `$3.990 CLP / mes` for a 30-day interval.
 *
 * @param amount Plan amount in the local currency (integer).
 * @param currency Currency code (e.g. "CLP").
 * @param intervalDays Billing interval in days (30 → "/ mes").
 * @returns Formatted string, or null when no amount is available.
 */
const formatBillingAmount = (
  amount: number | null,
  currency: string | null,
  intervalDays: number | null
): string | null => {
  if (amount == null) return null;
  const base = `$${amount.toLocaleString("es-CL")} ${currency ?? "CLP"}`;
  if (intervalDays === 30) return `${base} / mes`;
  if (intervalDays) return `${base} / ${intervalDays} días`;
  return base;
};

/**
 * Formats the discounted amount for a corporate benefit: base plan amount
 * minus the partner discount percent.
 *
 * @param amount Base plan amount (integer).
 * @param discountPercent Partner discount percent (0–100).
 * @param currency Currency code.
 * @param intervalDays Billing interval in days.
 * @returns Formatted string, or null when amount/discount are unavailable.
 */
const formatDiscountedAmount = (
  amount: number | null,
  discountPercent: number | null,
  currency: string | null,
  intervalDays: number | null
): string | null => {
  if (amount == null || discountPercent == null) return null;
  const discounted = Math.round(amount * (1 - discountPercent / 100));
  return formatBillingAmount(discounted, currency, intervalDays);
};

/** Renders the subscription settings card with the differentiated states. */
export const SubscriptionSettings: React.FC = () => {
  const { data, error, loading } = useFetch<SubscriptionSettingsResponse>(
    endPointSubscriptionSettings
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isAcquirePlusOpen, setIsAcquirePlusOpen] = useState(false);
  const [pendingCancellation, setPendingCancellation] =
    useState<PendingCancellation | null>(null);

  const handleConfirmCancel = async (): Promise<void> => {
    setCancelling(true);
    posthog.capture("cancel-subscription");
    try {
      const result = await apiRequest<CancelResult>(endPointSubscriptionCancel, {
        method: "POST",
      });
      const endsAt = formatSubscriptionEndDate(result.ends_at);
      setPendingCancellation({ endsAt });
      setConfirmOpen(false);
      toast.success(`Tu suscripción termina el ${endsAt}.`);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No pudimos cancelar tu suscripción.";
      toast.error(message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Suscripción</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">Cargando tu suscripción...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Suscripción</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500">
            No pudimos cargar tu información de suscripción. Inténtalo de nuevo más tarde.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Suscripción</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No hay información de suscripción disponible.</p>
        </CardContent>
      </Card>
    );
  }

  const isCorporate = data.payment_source === "corporate";
  const isUserPaid = data.payment_source === "user_paid";
  const isCancellationPending = data.cancellation_pending;
  const showCancelledState =
    isCancellationPending || pendingCancellation !== null;
  const isActiveUserPaid = isUserPaid && !showCancelledState;
  const canCancelSubscription = data.can_cancel && !showCancelledState;
  const endDateValue = data.premium_expires_at ?? pendingCancellation?.endsAt;
  const corporateDiscount = data.corporate_benefit?.discount_percent ?? null;
  const discountedAmount = formatDiscountedAmount(
    data.plan_amount,
    corporateDiscount,
    data.currency,
    data.interval_days
  );

  const badgeLabel = !data.premium_user
    ? "Free"
    : showCancelledState
      ? "Cancelada"
      : "Activa";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Suscripción</CardTitle>
        <CardDescription>
          Tu plan y cómo accedes a Carriapp Plus
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge
            variant={
              !data.premium_user || showCancelledState ? "secondary" : "default"
            }
          >
            {badgeLabel === "Activa" && (
              <span
                data-testid="status-badge-dot"
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-current"
              />
            )}
            {badgeLabel}
          </Badge>
        </div>

        {!data.premium_user && (
          <p className="text-muted-foreground text-sm">
            Estás en el plan Free. Mejora a Plus para desbloquear beneficios.
          </p>
        )}

        {data.premium_user && isCorporate && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {data.corporate_benefit?.partner_logo_url && (
                <img
                  src={data.corporate_benefit.partner_logo_url}
                  alt={`Logo de ${data.corporate_benefit.partner_name ?? "empresa asociada"}`}
                  className="h-10 w-10 rounded-md border object-contain p-1"
                />
              )}
              <div className="space-y-1">
                <p className="text-sm font-medium">Beneficio corporativo</p>
                <p className="text-muted-foreground text-sm">
                  Tu acceso a Plus es un beneficio de{" "}
                  {data.corporate_benefit?.partner_name ?? "tu empresa"}.
                </p>
              </div>
            </div>
            {discountedAmount && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Monto</span>
                  <span className="font-medium">{discountedAmount}</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Beneficio otorgado por{" "}
                  {data.corporate_benefit?.partner_name ?? "tu empresa"}.
                </p>
              </div>
            )}
          </div>
        )}

        {data.premium_user && showCancelledState && (
          <div className="space-y-3">
            <p className="text-sm">{CANCELLED_COPY}</p>
            <Separator />
            <p className="text-sm font-medium">
              Tu acceso a Plus termina el {formatLongDate(endDateValue)}.
            </p>
            <Button variant="default" onClick={() => setIsAcquirePlusOpen(true)}>
              Reactivar Plus
            </Button>
          </div>
        )}

        {data.premium_user && isActiveUserPaid && (
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Se renueva automáticamente.
            </p>
            {(data.plan_amount != null || data.next_billing_date != null) && (
              <div className="space-y-1">
                {data.plan_amount != null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Monto</span>
                    <span className="font-medium">
                      {formatBillingAmount(
                        data.plan_amount,
                        data.currency,
                        data.interval_days
                      )}
                    </span>
                  </div>
                )}
                {data.next_billing_date != null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Próximo cobro</span>
                    <span className="font-medium">
                      {formatLongDate(data.next_billing_date)}
                    </span>
                  </div>
                )}
              </div>
            )}
            <Separator />
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
            >
              Cancelar acceso Plus
            </Button>
          </div>
        )}

        {data.premium_user && canCancelSubscription && !isActiveUserPaid && (
          <div className="space-y-3">
            <Separator />
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
            >
              Cancelar acceso Plus
            </Button>
          </div>
        )}

        {data.premium_user &&
          !isCorporate &&
          !isActiveUserPaid &&
          !showCancelledState && (
            <p className="text-muted-foreground text-sm">
              Tienes acceso a Plus.
            </p>
          )}
      </CardContent>

      {data.premium_user && canCancelSubscription && (
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Cancelar tu suscripción?</AlertDialogTitle>
              <AlertDialogDescription>
                Tu acceso a Plus continuará hasta el final del período de
                facturación actual y tu tarjeta será desvinculada.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={cancelling}>
                Mantener suscripción
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={cancelling}
                onClick={(event) => {
                  event.preventDefault();
                  void handleConfirmCancel();
                }}
              >
                {cancelling ? "Cancelando..." : "Sí, cancelar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <AcquirePlusDialog
        open={isAcquirePlusOpen}
        onOpenChange={setIsAcquirePlusOpen}
      />
    </Card>
  );
};
